import React, { useEffect, useState, useCallback } from 'react';
import { memberSponsorshipAPI, brandingAPI } from '../lib/api';import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Loader2, Search, Copy, RefreshCw, FileDown, Mail, CheckCircle, RotateCcw,
  Trash2, Users, Heart, ChevronLeft, ChevronRight, AlertTriangle, Link as LinkIcon,
  Pencil, Save, X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const STATUS_BADGE = {
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  yet_to_pay: 'bg-amber-100 text-amber-700 border-amber-200',
};

function MemberRow({ it, emailingId, togglePaid, downloadInvoice, emailInvoice, setDeleteTarget }) {
  const isPaid = it.payment_status === 'paid';
  return (
    <tr className="border-t border-border align-top" data-testid={`member-row-${it.id}`}>
      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
        {it.created_at ? format(parseISO(it.created_at), 'd MMM yyyy') : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{it.name}</div>
        <div className="text-xs text-muted-foreground">{it.email}</div>
        {it.phone && <div className="text-xs text-muted-foreground">{it.phone}</div>}
        {it.notes && <div className="text-xs text-muted-foreground italic mt-1">{it.notes}</div>}
      </td>
      <td className="px-4 py-3 font-medium whitespace-nowrap">
        AUD ${Number(it.amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={`${STATUS_BADGE[it.payment_status] || ''} capitalize`}>
          {isPaid ? 'Paid' : 'Yet to pay'}
        </Badge>
        {isPaid && it.paid_at && (
          <div className="text-xs text-muted-foreground mt-1">
            {format(parseISO(it.paid_at), 'd MMM yyyy')}
            {it.paid_by_admin_email && <> · by {it.paid_by_admin_email}</>}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground font-mono">
        {it.invoice_number || '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          <Button
            size="sm"
            variant={isPaid ? 'outline' : 'default'}
            onClick={() => togglePaid(it)}
            className="gap-1"
            data-testid={`mark-paid-${it.id}`}
            title={isPaid ? 'Revert payment status' : 'Confirm payment received'}
          >
            {isPaid ? (
              <><RotateCcw className="w-3.5 h-3.5" /> Revert</>
            ) : (
              <><CheckCircle className="w-3.5 h-3.5" /> Payment Received</>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadInvoice(it)}
            disabled={!isPaid}
            title={isPaid ? 'Download tax invoice PDF' : 'Mark Payment Received first to enable'}
            data-testid={`download-invoice-${it.id}`}
            className="gap-1"
          >
            <FileDown className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Download invoice</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => emailInvoice(it)}
            disabled={!isPaid || emailingId === it.id}
            title={isPaid ? 'Email tax invoice to member' : 'Mark Payment Received first to enable'}
            data-testid={`email-invoice-${it.id}`}
            className="gap-1"
          >
            {emailingId === it.id ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Mail className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">Email invoice</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDeleteTarget(it)}
            className="text-destructive hover:text-destructive"
            title="Delete"
            data-testid={`delete-contribution-${it.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function MemberSponsorship() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ paid_count: 0, pending_count: 0, paid_total: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [branding, setBranding] = useState(null);
  const [presetEditing, setPresetEditing] = useState(false);
  const [presetDraft, setPresetDraft] = useState('');
  const [presetSaving, setPresetSaving] = useState(false);
  const [emailingId, setEmailingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const publicLink = `${window.location.origin}/member-sponsorship/submit`;

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== 'all') params.payment_status = statusFilter;
      if (searchQ.trim()) params.q = searchQ.trim();
      const res = await memberSponsorshipAPI.list(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
      setSummary(res.data.summary || { paid_count: 0, pending_count: 0, paid_total: 0 });
    } catch (e) {
      toast.error('Failed to load contributions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchQ]);

  useEffect(() => {
    brandingAPI.getPublic().then((r) => {
      setBranding(r.data);
      setPresetDraft(String(r.data.member_sponsorship_default_amount ?? 365));
    }).catch(() => {});
  }, []);

  const savePreset = async () => {
    const v = parseFloat(presetDraft);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Enter a valid amount greater than zero');
      return;
    }
    setPresetSaving(true);
    try {
      const res = await brandingAPI.update({ member_sponsorship_default_amount: v });
      setBranding((b) => ({ ...(b || {}), member_sponsorship_default_amount: v, ...(res.data || {}) }));
      setPresetEditing(false);
      toast.success('Recommended amount updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setPresetSaving(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const togglePaid = async (item) => {
    const target = item.payment_status === 'paid' ? 'yet_to_pay' : 'paid';
    try {
      const res = await memberSponsorshipAPI.update(item.id, { payment_status: target });
      if (target === 'paid') {
        const emailInfo = res?.data?.auto_invoice_email;
        if (emailInfo?.sent) {
          toast.success(`Marked as paid — tax invoice emailed to ${emailInfo.to || item.email}`);
        } else if (emailInfo && !emailInfo.sent) {
          toast.warning(
            `Marked as paid, but auto-email failed: ${emailInfo.error || 'unknown error'}. Use the Email invoice button to retry.`,
            { duration: 7000 }
          );
        } else {
          toast.success('Marked as paid');
        }
      } else {
        toast.success('Reverted to unpaid');
      }
      fetchPage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const downloadInvoice = async (item) => {
    try {
      await memberSponsorshipAPI.downloadInvoice(item.id, item.name);
      toast.success('Tax invoice downloaded');
      fetchPage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const emailInvoice = async (item) => {
    setEmailingId(item.id);
    try {
      const res = await memberSponsorshipAPI.emailInvoice(item.id);
      toast.success(res.data?.message || `Invoice emailed to ${item.email}`);
      fetchPage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to email invoice');
    } finally {
      setEmailingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await memberSponsorshipAPI.delete(deleteTarget.id);
      toast.success('Contribution deleted');
      setDeleteTarget(null);
      fetchPage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      toast.success('Public form link copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  return (
    <Layout activeNav="member-sponsorship">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1 flex items-center gap-2">
              <Heart className="w-7 h-7 text-rose-500" />
              Member Sponsorship
            </h1>
            <p className="text-muted-foreground">
              Tax-deductible contributions from IIDA members.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={copyPublicLink}
              className="gap-2"
              data-testid="copy-public-link-btn"
            >
              <Copy className="w-4 h-4" />
              Copy public form link
            </Button>
            <Button
              variant="outline"
              onClick={fetchPage}
              disabled={loading}
              className="gap-2"
              data-testid="member-refresh-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total submissions</p>
              <p className="text-2xl font-bold mt-1" data-testid="summary-total">{total}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Paid</p>
              <p className="text-2xl font-bold mt-1 text-emerald-600" data-testid="summary-paid-count">{summary.paid_count}</p>
              <p className="text-xs text-muted-foreground">AUD ${(summary.paid_total || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold mt-1 text-amber-600" data-testid="summary-pending-count">{summary.pending_count}</p>
            </CardContent>
          </Card>
        </div>

        {/* Public link callout */}
        <Card className="border-border mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Public submission link
            </CardTitle>
            <CardDescription>
              Share this link with members so they can submit their annual contribution. Bank details are shown to them on the form.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Input value={publicLink} readOnly className="font-mono text-xs" data-testid="public-link-input" />
              <Button variant="outline" size="sm" onClick={copyPublicLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {/* Recommended-contribution-amount admin editor */}
            <div className="mt-4 rounded-lg border border-border bg-muted/30 px-3 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="suggested-amount-row">
              <div className="text-sm">
                <span className="font-medium text-foreground">Recommended contribution amount</span>
                <span className="text-muted-foreground"> · shown as the default option on the public form (members can also choose "Other").</span>
              </div>
              {presetEditing ? (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={presetDraft}
                      onChange={(e) => setPresetDraft(e.target.value)}
                      className="h-9 w-32 pl-5 text-sm"
                      data-testid="suggested-amount-input"
                      autoFocus
                    />
                  </div>
                  <Button size="sm" onClick={savePreset} disabled={presetSaving} data-testid="suggested-amount-save">
                    {presetSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setPresetEditing(false); setPresetDraft(String(branding?.member_sponsorship_default_amount ?? 365)); }} data-testid="suggested-amount-cancel">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-foreground" data-testid="suggested-amount-value">
                    AUD ${Number(branding?.member_sponsorship_default_amount ?? 365).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => setPresetEditing(true)} className="gap-1" data-testid="suggested-amount-edit">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                </div>
              )}
            </div>

            {(branding?.bank_bsb || branding?.bank_account_number) && (
              <div className="text-xs text-muted-foreground mt-3 grid sm:grid-cols-3 gap-1">
                <p><span className="font-medium text-foreground">Account:</span> {branding.bank_account_name}</p>
                <p><span className="font-medium text-foreground">BSB:</span> {branding.bank_bsb}</p>
                <p><span className="font-medium text-foreground">Acct #:</span> {branding.bank_account_number}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filter bar */}
        <Card className="border-border mb-6">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-[200px_1fr] gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="h-10" data-testid="ms-status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({total})</SelectItem>
                    <SelectItem value="paid">Paid ({summary.paid_count})</SelectItem>
                    <SelectItem value="yet_to_pay">Yet to pay ({summary.pending_count})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="name, email or phone..."
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchPage(); } }}
                    className="h-10 pl-9"
                    data-testid="ms-search-input"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contributions table */}
        <Card className="border-border">
          <CardContent className="p-0">
            {loading && items.length === 0 ? (
              <div className="p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground" data-testid="member-empty">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No member contributions yet.</p>
                <p className="text-sm mt-1">Share the public link to start collecting.</p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="ms-contributions-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-medium">Submitted</th>
                      <th className="px-4 py-3 font-medium">Member</th>
                      <th className="px-4 py-3 font-medium">Amount</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Invoice</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <MemberRow
                        key={it.id}
                        it={it}
                        emailingId={emailingId}
                        togglePaid={togglePaid}
                        downloadInvoice={downloadInvoice}
                        emailInvoice={emailInvoice}
                        setDeleteTarget={setDeleteTarget}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm" data-testid="member-pagination">
            <p className="text-muted-foreground">Page {page} of {totalPages} · {total} entries</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="member-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Delete contribution?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete the submission from <strong>{deleteTarget?.name}</strong>?
              The audit log entry will be retained for compliance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
